import { useId } from "react";
import { cn } from "@/lib/cn";

const DENKER_SYMBOL_PATH =
  "M274.645,533.768C280.365,528.034 282.076,519.409 278.98,511.916C275.884,504.424 268.592,499.538 260.503,499.538L150,499.538L150,469.462L300,469.462C316.569,469.462 330,455.996 330,439.385L330,289L360,289L360,399.786C360,407.896 364.873,415.207 372.346,418.311C379.82,421.414 388.422,419.699 394.142,413.964C427.176,380.846 472.279,335.627 472.279,335.627L493.492,356.894C493.492,356.894 448.389,402.114 415.355,435.232C409.635,440.966 407.924,449.591 411.02,457.084C414.116,464.576 421.408,469.462 429.497,469.462L540,469.462L540,499.538L390,499.538C373.431,499.538 360,513.004 360,529.615L360,680L330,680L330,569.214C330,561.104 325.127,553.793 317.654,550.689C310.18,547.586 301.578,549.301 295.858,555.036C262.824,588.154 217.721,633.373 217.721,633.373L196.508,612.106C196.508,612.106 241.611,566.886 274.645,533.768Z";

function svgId(rawId: string, suffix: string) {
  return `${rawId.replace(/:/g, "")}-${suffix}`;
}

interface LiquidDenkerMarkProps {
  size: number;
  className?: string;
  testId?: string;
}

export function LiquidDenkerMark({
  size,
  className,
  testId = "liquid-denker-mark",
}: LiquidDenkerMarkProps) {
  const id = useId();
  const fillId = svgId(id, "fill");
  const rimId = svgId(id, "rim");
  const shineId = svgId(id, "shine");
  const softEdgeId = svgId(id, "soft-edge");
  const glowId = svgId(id, "glow");

  return (
    <svg
      role="img"
      aria-label="Denker"
      className={cn("shrink-0 overflow-visible", className)}
      width={size}
      height={size}
      viewBox="0 0 780 780"
      data-testid={testId}
    >
      <defs>
        <linearGradient id={fillId} x1="152" y1="326" x2="520" y2="668">
          <stop
            offset="0"
            stopColor="var(--denker-intro-mark-hi, #F9FEFF)"
            stopOpacity="0.68"
          />
          <stop
            offset="0.2"
            stopColor="var(--denker-intro-mark-soft, #C8F1FF)"
            stopOpacity="0.62"
          />
          <stop
            offset="0.45"
            stopColor="var(--denker-intro-mark-mid, #66BDF8)"
            stopOpacity="0.54"
          />
          <stop
            offset="0.68"
            stopColor="var(--denker-intro-mark-soft, #B4F4FF)"
            stopOpacity="0.44"
          />
          <stop
            offset="1"
            stopColor="var(--denker-intro-mark-hi, #F8FBFF)"
            stopOpacity="0.52"
          />
        </linearGradient>
        <linearGradient id={rimId} x1="150" y1="300" x2="540" y2="680">
          <stop
            offset="0"
            stopColor="var(--denker-intro-mark-hi, #FFFFFF)"
            stopOpacity="0.78"
          />
          <stop
            offset="0.36"
            stopColor="var(--denker-intro-mark-soft, #DDF8FF)"
            stopOpacity="0.34"
          />
          <stop
            offset="0.72"
            stopColor="var(--denker-intro-mark-mid, #9EE7FF)"
            stopOpacity="0.28"
          />
          <stop
            offset="1"
            stopColor="var(--denker-intro-mark-hi, #FFFFFF)"
            stopOpacity="0.58"
          />
        </linearGradient>
        <radialGradient
          id={shineId}
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="matrix(210 96 -110 240 286 395)"
        >
          <stop
            offset="0"
            stopColor="var(--denker-intro-mark-hi, #FFFFFF)"
            stopOpacity="0.95"
          />
          <stop
            offset="0.38"
            stopColor="var(--denker-intro-mark-soft, #D8F2FF)"
            stopOpacity="0.34"
          />
          <stop
            offset="1"
            stopColor="var(--denker-intro-mark-hi, #FFFFFF)"
            stopOpacity="0"
          />
        </radialGradient>
        <filter id={softEdgeId} x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow
            dx="0"
            dy="14"
            stdDeviation="12"
            floodColor="#001616"
            floodOpacity="0.34"
          />
          <feDropShadow
            dx="0"
            dy="-8"
            stdDeviation="8"
            floodColor="#FFFFFF"
            floodOpacity="0.18"
          />
        </filter>
        <filter id={glowId} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="10" result="blur" />
          <feColorMatrix
            in="blur"
            type="matrix"
            values="0 0 0 0 0.45 0 0 0 0 0.83 0 0 0 0 1 0 0 0 0.46 0"
          />
        </filter>
      </defs>
      <g transform="matrix(1,0,0,1,-2200,0)">
        <g transform="matrix(0.404984,0,0,0.44546,2200,0)">
          <g transform="matrix(4.938462,0,0,4.478261,-740.769231,-1294.217391)">
            <path
              d={DENKER_SYMBOL_PATH}
              fill="var(--denker-intro-mark-mid, #7DD3FC)"
              filter={`url(#${glowId})`}
              opacity="0.42"
            />
            <path
              d={DENKER_SYMBOL_PATH}
              fill={`url(#${fillId})`}
              filter={`url(#${softEdgeId})`}
              opacity="0.78"
            />
            <path d={DENKER_SYMBOL_PATH} fill={`url(#${shineId})`} opacity="0.36" />
            <path
              d={DENKER_SYMBOL_PATH}
              fill="none"
              stroke={`url(#${rimId})`}
              strokeLinejoin="round"
              strokeWidth="12"
              opacity="0.64"
            />
            <path
              d={DENKER_SYMBOL_PATH}
              fill="none"
              stroke="var(--denker-intro-mark-hi, #FFFFFF)"
              strokeLinejoin="round"
              strokeWidth="4"
              opacity="0.28"
            />
          </g>
        </g>
      </g>
    </svg>
  );
}

export type { LiquidDenkerMarkProps };
